import { Link } from "react-router-dom";
import { ArrowRight, Route, Users2, HeartHandshake, GraduationCap, ClipboardList } from "lucide-react";
import { T } from "@/lib/testIds";

const HIGHLIGHTS = [
  { Icon: Route, title: "Meaningful routes", desc: "Help patients reach appointments that matter." },
  { Icon: Users2, title: "Team of professionals", desc: "Work alongside experienced, respectful drivers." },
  { Icon: HeartHandshake, title: "Compassion-first culture", desc: "Every ride is an opportunity to care." },
  { Icon: GraduationCap, title: "Support & orientation", desc: "Onboarding to help you succeed from day one." },
];

const STEPS = [
  { n: 1, t: "Apply online", d: "Complete our online driver application." },
  { n: 2, t: "Document review", d: "We review your license, vehicle, and history." },
  { n: 3, t: "Interview", d: "Meet the team and discuss the role." },
  { n: 4, t: "Welcome aboard", d: "Get set up and start driving with us." },
];

export default function Careers() {
  return (
    <div className="animate-fade-up">
      <section className="relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-emerald-100/50 rounded-full blur-3xl -z-10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 text-emerald-700 px-4 py-1.5 text-xs font-semibold tracking-wide uppercase">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> We're Hiring
            </div>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#0A192F] leading-[1.05]">Drive With Us</h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">Join our team of professional drivers and help patients get to their medical appointments safely and comfortably.</p>
            <Link
              to="/apply"
              data-testid={T.careers.startApp}
              className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold shadow-lg shadow-emerald-500/25 transition-colors"
            >
              <ClipboardList className="w-4 h-4" /> Start Your Application <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="rounded-3xl overflow-hidden shadow-2xl border border-slate-200 bg-white">
            <img src="https://images.pexels.com/photos/31627609/pexels-photo-31627609.jpeg" alt="Professional NEMT driver" className="w-full h-[420px] object-cover" />
          </div>
        </div>
      </section>

      <section className="bg-slate-50 border-y border-slate-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0A192F]">What you can expect</h2>
          <p className="mt-2 text-slate-600 max-w-2xl">A professional environment focused on safety, reliability, and quality service. Specific benefits and compensation are discussed during the interview process.</p>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {HIGHLIGHTS.map(({ Icon, title, desc }) => (
              <div key={title} className="rounded-2xl bg-white border border-slate-200 p-6 hover:shadow-lg transition-shadow">
                <div className="w-11 h-11 rounded-xl bg-blue-50 text-[#1D4ED8] flex items-center justify-center"><Icon className="w-5 h-5" /></div>
                <h3 className="mt-4 font-semibold text-[#0A192F]">{title}</h3>
                <p className="mt-1.5 text-sm text-slate-600">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h2 className="text-3xl sm:text-4xl font-bold text-[#0A192F]">How the process works</h2>
        <div className="mt-10 grid md:grid-cols-4 gap-5">
          {STEPS.map((s) => (
            <div key={s.n} className="rounded-2xl bg-white border border-slate-200 p-6">
              <div className="w-10 h-10 rounded-full bg-[#0A192F] text-white font-bold flex items-center justify-center">{s.n}</div>
              <div className="mt-4 font-semibold text-[#0A192F]">{s.t}</div>
              <p className="mt-1.5 text-sm text-slate-600">{s.d}</p>
            </div>
          ))}
        </div>
        <div className="mt-12 rounded-3xl bg-gradient-to-br from-[#0A192F] to-[#12244a] text-white p-10 text-center relative overflow-hidden">
          <div className="absolute -right-16 -bottom-16 w-72 h-72 bg-emerald-500/20 rounded-full blur-3xl" />
          <h3 className="text-2xl sm:text-3xl font-bold relative">Ready to apply?</h3>
          <p className="mt-2 text-slate-300 relative">Start your application — takes just a few minutes.</p>
          <Link to="/apply" className="mt-6 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white font-semibold relative">
            Start Your Application <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
